import { createContext, useContext, useState } from 'react';
import { useT } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { initials, useLiveDraft, type LiveDraft } from './live';

// Live text fields: what the others type in a field shows under it as they
// type, before they save (see useLiveDraft). A field is made live with a full
// key (liveKey="task:12:description") or, inside a <LiveScope> (a project's
// page: "project:7"), with just its name (liveField="problem").

const hueStyle = (userId: number) => ({ ['--live-hue' as string]: String((userId * 67) % 360) });

const ScopeContext = createContext<string | null>(null);

/** Gives the live fields inside a key prefix ("project:7"); null: not live. */
export function LiveScope({ prefix, children }: { prefix: string | null; children: React.ReactNode }) {
  return <ScopeContext.Provider value={prefix}>{children}</ScopeContext.Provider>;
}

/** A field's live key: liveKey as given, or liveField inside the current LiveScope. */
export function useLiveKey(liveKey?: string | null, liveField?: string | null): string | null {
  const prefix = useContext(ScopeContext);
  if (liveKey) return liveKey;
  return liveField && prefix ? `${prefix}:${liveField}` : null;
}

/**
 * For a text field: its focus handlers and what the others type in it.
 * key null: not live (the handlers just call through).
 */
export function useLiveField(key: string | null, value: string, onFocus?: () => void, onBlur?: () => void) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const drafts = useLiveDraft(key, value, editing, user?.id);
  return {
    drafts,
    onFocus: () => { setEditing(true); onFocus?.(); },
    onBlur: () => { setEditing(false); onBlur?.(); },
  };
}

/** What the others are typing in a field, under it: their initials and their text. */
export function LiveDrafts({ drafts }: { drafts: LiveDraft[] }) {
  const { t } = useT();
  if (drafts.length === 0) return null;
  return (
    <div className="live-drafts">
      {drafts.map(d => (
        <div key={d.userId} className="live-draft" style={hueStyle(d.userId)} aria-live="polite">
          <div className="live-draft-who">
            <span className="live-avatar live-avatar--sm">{initials(d.name)}</span>
            {t('live.typing', { name: d.name })}<span className="live-draft-dots" aria-hidden="true" />
          </div>
          {d.text.trim() && <div className="live-draft-text">{d.text}</div>}
        </div>
      ))}
    </div>
  );
}

/** For a field shown read-only: what the others type in it, live. */
export function LiveDraftView({ liveKey, liveField }: { liveKey?: string | null; liveField?: string | null }) {
  const key = useLiveKey(liveKey, liveField);
  const { user } = useAuth();
  const drafts = useLiveDraft(key, '', false, user?.id);
  return <LiveDrafts drafts={drafts} />;
}

/** On a board card: who is typing in it right now (✎ and their initials). */
export function TypingBadge({ people }: { people: { userId: number; name: string }[] | undefined }) {
  const { t } = useT();
  if (!people?.length) return null;
  return (
    <span className="live-focus live-typing">
      {people.map(p => (
        <span key={p.userId} className="live-avatar live-avatar--sm" style={hueStyle(p.userId)} title={t('live.typingOnCard', { name: p.name })}>
          {initials(p.name)} ✎
        </span>
      ))}
    </span>
  );
}
